<mat-sidenav-container class="sidenav-container" [hasBackdrop]="isHandset" (backdropClick)="closeDrawerOnHandset()">
  <mat-sidenav
    #drawer
    [mode]="isHandset ? 'over' : 'side'"
    [opened]="opened"
    class="sidenav"
    [style.width.px]="drawerWidth"
  >
    <div class="drawer-header">
      <h3 class="drawer-title">Table of Contents</h3>
    </div>
    @if (loading || loadingFileMetadata) {
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    }

    <!-- Project > file > page tree (Issue #2228) -->
    @if (!loading && !loadingFileMetadata && fileGroups.length > 0) {
      <div #drawerListScroll class="drawer-list-scroll" (scroll)="onDrawerTreeScrollEvent()">
        <div
          #projectTree
          class="project-tree"
          role="tree"
          [attr.aria-label]="projectDisplayName + ' table of contents'"
          [@.disabled]="prefersReducedMotion"
        >
          @let overlayFile = activeTreeFile;
          @let overlayPage = activeTreePage;
          @if (stickyAncestorStackVisible && overlayFile && overlayPage) {
            @let stickyBaseAncestorCount = getStickyBaseAncestorCount(overlayFile);
            @let hasLaterFile = overlayFile !== fileGroups[fileGroups.length - 1];
            @let hasLaterPage = overlayPage !== overlayFile.pages[overlayFile.pages.length - 1];
            <div
              class="tree-sticky-ancestor-overlay"
              data-testid="tree-sticky-ancestor-overlay"
              aria-hidden="true"
              [class.scroll-under]="stickyStackScrollUnder"
            >
              <div class="tree-sticky-clone tree-sticky-project-clone tree-node-row" style="--tree-node-depth: 0">
                <span class="tree-disclosure" [attr.aria-expanded]="projectExpanded"
                  ><mat-icon aria-hidden="true" [class.expanded]="projectExpanded">chevron_right</mat-icon></span
                >
                <span class="tree-child-tail" aria-hidden="true"></span>
                <span class="project-label-action">
                  <mat-icon class="project-icon" fontSet="material-symbols-outlined" aria-hidden="true">house</mat-icon>
                  <span class="project-info">
                    <span class="project-name">{{ projectDisplayName }}</span>
                    <span class="project-subtitle">
                      Project · {{ fileGroups.length }} {{ fileGroups.length === 1 ? 'file' : 'files' }} ·
                      {{ totalTreePageCount }} {{ totalTreePageCount === 1 ? 'page' : 'pages' }}
                    </span>
                  </span>
                </span>
              </div>
              @if (stickyBaseAncestorCount >= 2) {
                <div class="tree-sticky-clone tree-sticky-file-clone tree-node-row" style="--tree-node-depth: 1">
                  @if (hasLaterFile) {
                    <span
                      class="tree-sticky-sibling-continuation tree-sticky-file-sibling-rail owner-depth"
                      aria-hidden="true"
                    ></span>
                  }
                  <span class="tree-parent-connector" aria-hidden="true"></span>
                  <span class="tree-disclosure" [attr.aria-expanded]="overlayFile.expanded"
                    ><mat-icon aria-hidden="true" [class.expanded]="overlayFile.expanded">chevron_right</mat-icon></span
                  >
                  <span class="tree-child-tail" aria-hidden="true"></span>
                  <mat-icon class="file-type-icon" fontSet="material-symbols-outlined" aria-hidden="true"
                    >picture_as_pdf</mat-icon
                  >
                  <span class="file-label-action">
                    <span class="file-name">{{ overlayFile.fileName }}</span>
                    <span class="file-subtitle">{{ getFileSubtitle(overlayFile) }}</span>
                  </span>
                </div>
              }
              @if (stickyBaseAncestorCount >= 3) {
                <div
                  class="tree-sticky-clone tree-sticky-page-clone tree-node-row"
                  [class.tree-node-selected]="!activeRegionPath"
                  [class.checked]="isPageChecked(overlayPage, overlayFile.fileId)"
                  style="--tree-node-depth: 2"
                >
                  @if (hasLaterFile) {
                    <span
                      class="tree-sticky-sibling-continuation tree-sticky-file-sibling-rail"
                      aria-hidden="true"
                    ></span>
                  }
                  @if (hasLaterPage) {
                    <span
                      class="tree-sticky-sibling-continuation tree-sticky-page-sibling-rail owner-depth"
                      aria-hidden="true"
                    ></span>
                  }
                  <span class="tree-parent-connector" aria-hidden="true"></span>
                  @if (powerUserMode) {
                    <span
                      class="tree-disclosure page-region-disclosure"
                      [attr.aria-expanded]="isRegionExpanded(overlayPage, overlayFile.fileId)"
                    >
                      <mat-icon
                        class="region-expand-icon"
                        [class.expanded]="isRegionExpanded(overlayPage, overlayFile.fileId)"
                        aria-hidden="true"
                        >chevron_right</mat-icon
                      >
                    </span>
                  }
                  <span
                    class="tree-child-tail"
                    [class.tree-sticky-neutral-child-tail]="!activeRegionPath"
                    aria-hidden="true"
                  ></span>
                  <span class="page-selection-control overlay-area">
                    <span class="page-sheet-identity" aria-hidden="true">
                      <mat-icon class="page-type-icon" fontSet="material-symbols-outlined">draft</mat-icon>
                      <span class="page-number-badge">{{ overlayPage.getPageNumber() }}</span>
                    </span>
                    <mat-checkbox
                      class="page-checkbox overlay-checkbox"
                      tabindex="-1"
                      aria-hidden="true"
                      [checked]="isPageChecked(overlayPage, overlayFile.fileId)"
                    ></mat-checkbox>
                  </span>
                  <span class="page-content">
                    <span class="page-title-area">
                      <span class="page-title">{{ overlayPage.getTitle() }}</span>
                      @if (overlayPage.getPageId()) {
                        <span class="page-subtitle">
                          <span class="tree-sticky-sheet-chip">{{ overlayPage.getPageId() }}</span>
                          <span class="sheet-discipline">{{ getSheetDiscipline(overlayPage.getPageId()) }}</span>
                        </span>
                      }
                    </span>
                  </span>
                  @if (pageStatus(overlayPage, overlayFile.fileId); as status) {
                    @if (pageWarningMarkers(status); as markers) {
                      @if (markers.length > 0) {
                        <span class="page-warning-markers">
                          @for (marker of markers; track marker.risk) {
                            <span
                              class="app-compliance-count-chip page-warning-marker"
                              [ngClass]="marker.className"
                              [attr.aria-label]="marker.label"
                              [matTooltip]="marker.label"
                              >{{ marker.count }}</span
                            >
                          }
                        </span>
                      }
                    }
                    @if (hasPageCoverageStatus(status)) {
                      <span
                        class="page-status-marker"
                        [ngClass]="pageCoverageClass(status)"
                        [attr.aria-label]="pageCoverageLabel(status)"
                        [matTooltip]="pageCoverageLabel(status)"
                        >{{ pageCoverageShortLabel(status) }}</span
                      >
                    }
                  }
                </div>
                @let stickyRegionChain = getActiveRegionStickyChain(overlayPage, overlayFile.fileId);
                @let stickySiblingRailDepths =
                  getActiveRegionStickySiblingRailDepths(overlayPage, overlayFile.fileId, stickyRegionChain);
                @for (node of stickyRegionChain; track node.path; let depth = $index) {
                  @if (depth < pinnedRegionAncestorCount) {
                    <div
                      class="tree-sticky-clone region-sticky-ancestor-clone"
                      [attr.data-sticky-region-path]="node.path"
                      [style.--tree-region-overlay-depth]="depth"
                      (click)="$event.stopPropagation(); onRegionClick(overlayPage, overlayFile.fileId, node)"
                    >
                      @if (hasLaterFile) {
                        <span
                          class="tree-sticky-sibling-continuation tree-sticky-file-sibling-rail"
                          aria-hidden="true"
                        ></span>
                      }
                      @if (hasLaterPage) {
                        <span
                          class="tree-sticky-sibling-continuation tree-sticky-page-sibling-rail"
                          aria-hidden="true"
                        ></span>
                      }
                      @for (railDepth of stickySiblingRailDepths; track railDepth) {
                        @if (railDepth <= depth) {
                          <span
                            class="region-sticky-sibling-continuation"
                            [class.owner-depth]="railDepth === depth"
                            [style.--tree-sticky-sibling-depth]="railDepth"
                            aria-hidden="true"
                          ></span>
                        }
                      }
                      <div
                        class="region-list-row tree-node-row"
                        [class.tree-node-selected]="isActiveRegion(node, overlayPage, overlayFile.fileId)"
                        [style.--tree-node-depth]="3 + depth"
                      >
                        <span class="tree-parent-connector" aria-hidden="true"></span>
                        @if (node.childNodesList.length) {
                          <span
                            class="tree-disclosure region-child-expand-btn"
                            [attr.aria-expanded]="isRegionNodeExpanded(node, overlayPage, overlayFile.fileId)"
                            (click)="toggleRegionExpand(overlayPage, overlayFile.fileId, node, $event)"
                          >
                            <mat-icon
                              [class.expanded]="isRegionNodeExpanded(node, overlayPage, overlayFile.fileId)"
                              aria-hidden="true"
                              >chevron_right</mat-icon
                            >
                          </span>
                          @if (depth + 1 < stickyRegionChain.length) {
                            <span class="tree-child-tail" aria-hidden="true"></span>
                          } @else if (isRegionNodeExpanded(node, overlayPage, overlayFile.fileId)) {
                            <span class="tree-child-tail tree-sticky-neutral-child-tail" aria-hidden="true"></span>
                          }
                        } @else {
                          <span class="region-leaf-spacer region-leaf-icon-frame" aria-hidden="true">
                            <mat-icon class="region-leaf-icon" fontSet="material-symbols-outlined">article</mat-icon>
                          </span>
                        }
                        <div class="region-title-area">
                          <span class="region-title">{{ node.title || node.slug }}</span>
                          @if (node.ncsClassification) {
                            <span class="region-subtitle">{{ node.ncsClassification }}</span>
                          }
                        </div>
                      </div>
                    </div>
                  }
                }
              }
            </div>
          }
          <div
            class="project-root tree-branch"
            role="treeitem"
            aria-level="1"
            [attr.aria-expanded]="projectExpanded"
            [attr.aria-selected]="projectSelected"
            data-tree-key="project"
            [attr.tabindex]="isTreeItemFocused('project') ? 0 : -1"
            [class.tree-collapsing]="projectCollapseAnimating"
            [class.active-path-root]="hasActivePage()"
            (focus)="onTreeItemFocus('project')"
            (keydown)="onTreeKeydown($event, 'project')"
          >
            <div
              class="project-root-row sticky-project-ancestor tree-node-row"
              [class.scroll-under]="semanticStickyScrollUnderOwner === 'project'"
              [class.tree-node-selected]="projectSelected"
              data-testid="sticky-project-ancestor"
              style="--tree-node-depth: 0"
            >
              <button
                type="button"
                class="tree-disclosure"
                tabindex="-1"
                [attr.aria-label]="(projectExpanded ? 'Collapse ' : 'Expand ') + projectDisplayName"
                [attr.aria-expanded]="projectExpanded"
                (click)="toggleProject($event)"
              >
                <mat-icon aria-hidden="true" [class.expanded]="projectExpanded">chevron_right</mat-icon>
              </button>
              <span class="tree-child-tail" aria-hidden="true"></span>
              <button
                type="button"
                class="project-label-action"
                tabindex="-1"
                [attr.aria-label]="'Open settings for ' + projectDisplayName"
                [appTruncatedTooltip]="projectDisplayName"
                appTruncatedTooltipTarget=".project-name"
                appTruncatedTooltipFocusOrigin=".project-root"
                (click)="openProjectSettings($event)"
              >
                <mat-icon class="project-icon" fontSet="material-symbols-outlined" aria-hidden="true">house</mat-icon>
                <span class="project-info">
                  <span class="project-name">{{ projectDisplayName }}</span>
                  <span class="project-subtitle">
                    Project · {{ fileGroups.length }} {{ fileGroups.length === 1 ? 'file' : 'files' }} ·
                    {{ totalTreePageCount }} {{ totalTreePageCount === 1 ? 'page' : 'pages' }}
                  </span>
                </span>
              </button>
            </div>

            @if (projectExpanded || projectTreeAnimating) {
              <div
                class="project-children"
                role="group"
                [class.project-children-expanded]="projectChildrenVisuallyExpanded"
                [class.project-children-animating]="projectTreeAnimating"
                [attr.inert]="!projectExpanded ? '' : null"
                [attr.aria-hidden]="!projectExpanded ? 'true' : null"
                (transitionend)="onProjectTreeTransitionEnd($event)"
              >
                <div class="project-children-content" role="presentation">
                  @for (fileGroup of fileGroups; track fileGroup.fileId; let isLastFile = $last) {
                    <div
                      class="file-treeitem tree-depth-node tree-branch"
                      role="treeitem"
                      aria-level="2"
                      [attr.aria-expanded]="fileGroup.expanded"
                      [attr.aria-selected]="fileGroup.fileId === activeFileId"
                      [attr.aria-label]="fileGroup.fileName + ', ' + getFileSubtitle(fileGroup)"
                      [attr.data-tree-key]="fileTreeKey(fileGroup.fileId)"
                      [attr.tabindex]="isTreeItemFocused(fileTreeKey(fileGroup.fileId)) ? 0 : -1"
                      data-testid="project-drawer-file"
                      [attr.data-file-id]="fileGroup.fileId"
                      [attr.data-file-name]="fileGroup.fileName"
                      [class.tree-collapsing]="collapsingFileIds.has(fileGroup.fileId)"
                      [class.active-path-before]="isFileBeforeActivePath(fileGroup)"
                      [class.active-path-file]="fileGroup.fileId === activeFileId && hasActivePage()"
                      [class.final-file-treeitem]="isLastFile"
                      [class.last-tree-sibling]="isLastFile"
                      style="--tree-node-depth: 1"
                      (focus)="onTreeItemFocus(fileTreeKey(fileGroup.fileId))"
                      (keydown)="onTreeKeydown($event, 'file', fileGroup)"
                    >
                      <div
                        class="file-row sticky-file-ancestor tree-node-row"
                        data-testid="sticky-file-ancestor"
                        [class.scroll-under]="
                          semanticStickyScrollUnderOwner === 'file' && fileGroup.fileId === activeFileId
                        "
                        [class.active-file]="fileGroup.fileId === activeFileId"
                        [class.tree-node-selected]="
                          fileLabelActivatesFile && fileGroup.fileId === activeFileId && activePageNumber === null
                        "
                      >
                        <button
                          type="button"
                          class="tree-disclosure"
                          tabindex="-1"
                          [attr.aria-label]="(fileGroup.expanded ? 'Collapse ' : 'Expand ') + fileGroup.fileName"
                          [attr.aria-expanded]="fileGroup.expanded"
                          (click)="toggleFile(fileGroup, $event)"
                        >
                          <mat-icon aria-hidden="true" [class.expanded]="fileGroup.expanded">chevron_right</mat-icon>
                        </button>
                        @if (!isLastFile) {
                          <span
                            class="tree-sticky-sibling-continuation tree-sticky-file-sibling-rail owner-depth"
                            aria-hidden="true"
                          ></span>
                        }
                        <span class="tree-parent-connector" aria-hidden="true"></span>
                        <span class="tree-child-tail" aria-hidden="true"></span>
                        <mat-icon class="file-type-icon" fontSet="material-symbols-outlined" aria-hidden="true"
                          >picture_as_pdf</mat-icon
                        >
                        <button
                          type="button"
                          class="file-label-action"
                          tabindex="-1"
                          [attr.aria-label]="'Open ' + fileGroup.fileName"
                          [appTruncatedTooltip]="fileGroup.fileName"
                          appTruncatedTooltipTarget=".file-name"
                          appTruncatedTooltipFocusOrigin=".file-treeitem"
                          (click)="onFileLabelClick(fileGroup, $event)"
                        >
                          <span class="file-name">{{ fileGroup.fileName }}</span>
                          <span class="file-subtitle">{{ getFileSubtitle(fileGroup) }}</span>
                        </button>
                      </div>

                      @if (!isLastFile) {
                        <span class="tree-sibling-continuation" aria-hidden="true"></span>
                      }

                      @if (fileGroup.expanded || animatingFileIds.has(fileGroup.fileId)) {
                        <div
                          class="file-children"
                          role="group"
                          [class.file-children-expanded]="fileGroup.expanded && !reversingFileIds.has(fileGroup.fileId)"
                          [class.file-children-animating]="animatingFileIds.has(fileGroup.fileId)"
                          [class.file-children-reconciling]="reconcilingFileIds.has(fileGroup.fileId)"
                          [class.file-children-motion-disabled]="disableNestedTreeAnimations"
                          [attr.inert]="!fileGroup.expanded ? '' : null"
                          [attr.aria-hidden]="!fileGroup.expanded ? 'true' : null"
                          (transitionend)="onFileTreeTransitionEnd(fileGroup, $event)"
                        >
                          <div class="file-children-content" role="presentation">
                            @for (
                              page of fileGroup.pages;
                              track trackByPageNumber($index, page);
                              let isLastPage = $last
                            ) {
                              @let navigationStatus = pageStatus(page, fileGroup.fileId);
                              <div
                                role="treeitem"
                                aria-level="3"
                                [attr.aria-expanded]="powerUserMode ? isRegionExpanded(page, fileGroup.fileId) : null"
                                [attr.aria-selected]="
                                  page.getPageNumber() === activePageNumber && fileGroup.fileId === activeFileId
                                "
                                [attr.aria-label]="
                                  'Page ' +
                                  page.getPageNumber() +
                                  ', ' +
                                  (page.getTitle() || 'Untitled page') +
                                  (navigationStatus ? pageStatusAriaSuffix(navigationStatus) : '')
                                "
                                [attr.data-tree-key]="pageTreeKey(fileGroup.fileId, page)"
                                [attr.tabindex]="isTreeItemFocused(pageTreeKey(fileGroup.fileId, page)) ? 0 : -1"
                                data-testid="project-drawer-page"
                                [attr.data-file-id]="fileGroup.fileId"
                                [attr.data-page-number]="page.getPageNumber()"
                                [attr.data-page-title]="page.getTitle()"
                                [attr.data-sheet-label]="page.getPageId()"
                                [appTruncatedTooltip]="page.getTitle()"
                                appTruncatedTooltipTarget=".page-title"
                                (focus)="onTreeItemFocus(pageTreeKey(fileGroup.fileId, page))"
                                (keydown)="onTreeKeydown($event, 'page', fileGroup, page)"
                                (click)="$event.stopPropagation(); onPageClick(page, fileGroup.fileId)"
                                [ngClass]="{
                                  checked: isPageChecked(page, fileGroup.fileId),
                                  active:
                                    page.getPageNumber() === activePageNumber &&
                                    fileGroup.fileId === activeFileId &&
                                    !activeRegionPath,
                                  'active-ancestor':
                                    page.getPageNumber() === activePageNumber &&
                                    fileGroup.fileId === activeFileId &&
                                    !!activeRegionPath,
                                  'active-path-ancestor': isPageBeforeActivePath(fileGroup, page),
                                  'selected-sticky-page':
                                    page.getPageNumber() === activePageNumber && fileGroup.fileId === activeFileId,
                                  'selected-active-page':
                                    page.getPageNumber() === activePageNumber && fileGroup.fileId === activeFileId,
                                }"
                                class="drawer-list-item-custom page-treeitem tree-depth-node tree-branch"
                                [class.last-page-treeitem]="isLastPage"
                                [class.last-tree-sibling]="isLastPage"
                                style="--tree-node-depth: 2"
                              >
                                <div
                                  class="drawer-list-row tree-node-row"
                                  [class.tree-node-selected]="
                                    page.getPageNumber() === activePageNumber &&
                                    fileGroup.fileId === activeFileId &&
                                    !activeRegionPath
                                  "
                                >
                                  @if (powerUserMode) {
                                    <button
                                      type="button"
                                      tabindex="-1"
                                      class="tree-disclosure page-region-disclosure region-expand-btn"
                                      (click)="toggleRegionHierarchy(page, fileGroup.fileId, $event)"
                                      (keydown)="onPageDisclosureKeydown(page, fileGroup.fileId, $event)"
                                      [attr.aria-label]="
                                        (isRegionExpanded(page, fileGroup.fileId) ? 'Collapse' : 'Expand') +
                                        ' region hierarchy for page ' +
                                        page.getPageNumber() +
                                        ': ' +
                                        (page.getTitle() || 'Untitled page')
                                      "
                                      [attr.aria-expanded]="isRegionExpanded(page, fileGroup.fileId)"
                                      matTooltip="Region hierarchy"
                                    >
                                      <mat-icon
                                        class="region-expand-icon"
                                        aria-hidden="true"
                                        [class.expanded]="isRegionExpanded(page, fileGroup.fileId)"
                                        >chevron_right</mat-icon
                                      >
                                    </button>
                                  }
                                  @if (page.getPageNumber() === activePageNumber && fileGroup.fileId === activeFileId) {
                                    @if (!isLastFile) {
                                      <span
                                        class="tree-sticky-sibling-continuation tree-sticky-file-sibling-rail"
                                        aria-hidden="true"
                                      ></span>
                                    }
                                    @if (!isLastPage) {
                                      <span
                                        class="tree-sticky-sibling-continuation tree-sticky-page-sibling-rail owner-depth"
                                        aria-hidden="true"
                                      ></span>
                                    }
                                  }
                                  <span class="tree-parent-connector" aria-hidden="true"></span>
                                  <span class="tree-child-tail" aria-hidden="true"></span>
                                  <span class="page-selection-control overlay-area">
                                    <span class="page-sheet-identity" aria-hidden="true">
                                      <mat-icon class="page-type-icon" fontSet="material-symbols-outlined"
                                        >draft</mat-icon
                                      >
                                      <span class="page-number-badge">{{ page.getPageNumber() }}</span>
                                    </span>
                                    <mat-checkbox
                                      class="page-checkbox overlay-checkbox"
                                      tabindex="-1"
                                      [aria-label]="
                                        'Select page ' +
                                        page.getPageNumber() +
                                        ', ' +
                                        (page.getTitle() || 'Untitled page')
                                      "
                                      [checked]="isPageChecked(page, fileGroup.fileId)"
                                      (click)="$event.stopPropagation()"
                                      (change)="onCheckboxChange(page, fileGroup.fileId)"
                                    ></mat-checkbox>
                                  </span>
                                  <div class="page-content">
                                    <div class="page-title-area">
                                      <span class="page-title">{{ page.getTitle() }}</span>
                                      @if (page.getPageId()) {
                                        <span class="page-subtitle">
                                          <mat-chip class="sheet-id-chip" color="primary" selected disableRipple>
                                            {{ page.getPageId() }}
                                          </mat-chip>
                                          <span class="sheet-discipline">{{
                                            getSheetDiscipline(page.getPageId())
                                          }}</span>
                                        </span>
                                      }
                                    </div>
                                  </div>
                                  @if (navigationStatus; as status) {
                                    @if (pageWarningMarkers(status); as markers) {
                                      @if (markers.length > 0) {
                                        <span class="page-warning-markers">
                                          @for (marker of markers; track marker.risk) {
                                            <span
                                              class="app-compliance-count-chip page-warning-marker"
                                              [ngClass]="marker.className"
                                              [attr.aria-label]="marker.label"
                                              [matTooltip]="marker.label"
                                              >{{ marker.count }}</span
                                            >
                                          }
                                        </span>
                                      }
                                    }
                                    @if (hasPageCoverageStatus(status)) {
                                      <span
                                        class="page-status-marker"
                                        [ngClass]="pageCoverageClass(status)"
                                        [attr.aria-label]="pageCoverageLabel(status)"
                                        [matTooltip]="pageCoverageLabel(status)"
                                        >{{ pageCoverageShortLabel(status) }}</span
                                      >
                                    }
                                  }
                                </div>
                                @if (!isLastPage) {
                                  <span class="tree-sibling-continuation" aria-hidden="true"></span>
                                }
                                @if (powerUserMode && isRegionExpanded(page, fileGroup.fileId)) {
                                  <div
                                    class="region-hierarchy-container"
                                    role="group"
                                    [attr.inert]="!isRegionExpanded(page, fileGroup.fileId) ? '' : null"
                                    [attr.aria-hidden]="!isRegionExpanded(page, fileGroup.fileId) ? 'true' : null"
                                    [@regionExpand]
                                  >
                                    @if (isRegionLoading(page, fileGroup.fileId)) {
                                      <mat-progress-bar
                                        mode="indeterminate"
                                        class="region-loading-bar"
                                      ></mat-progress-bar>
                                    }
                                    <ng-container
                                      *ngTemplateOutlet="
                                        regionTree;
                                        context: {
                                          $implicit: getRegionNodes(page, fileGroup.fileId),
                                          page: page,
                                          fileId: fileGroup.fileId,
                                          depth: 0,
                                        }
                                      "
                                    ></ng-container>
                                    @if (
                                      !isRegionLoading(page, fileGroup.fileId) &&
                                      getRegionNodes(page, fileGroup.fileId).length === 0
                                    ) {
                                      <div class="region-empty" [style.padding-left.px]="46">
                                        <span class="region-empty-text">No regions detected</span>
                                      </div>
                                    }
                                  </div>
                                }
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  }
                  <div
                    class="sticky-handoff-space"
                    data-testid="sticky-handoff-space"
                    aria-hidden="true"
                    [style.height.px]="stickyHandoffSpacePx"
                  ></div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Fallback: Flat list if no file metadata (legacy projects) -->
    @if (!loading && !loadingFileMetadata && fileGroups.length === 0 && pages.length > 0) {
      <div class="drawer-list-scroll">
        <mat-nav-list>
          @for (page of pages; track trackByPageNumber($index, page)) {
            <mat-list-item
              data-testid="project-drawer-page"
              data-file-id="legacy"
              [attr.data-page-number]="page.getPageNumber()"
              [attr.data-page-title]="page.getTitle()"
              [attr.data-sheet-label]="page.getPageId()"
              (click)="onActivate(page)"
              [ngClass]="{
                checked: isPageChecked(page),
                active: page.getPageNumber() === activePageNumber,
              }"
              class="drawer-list-item-custom"
            >
              <div class="drawer-list-row">
                <span class="page-selection-control overlay-area">
                  <span class="page-sheet-identity" aria-hidden="true">
                    <mat-icon class="page-type-icon" fontSet="material-symbols-outlined">draft</mat-icon>
                    <span class="page-number-badge">{{ page.getPageNumber() }}</span>
                  </span>
                  <mat-checkbox
                    class="page-checkbox overlay-checkbox"
                    [aria-label]="'Select page ' + page.getPageNumber() + ', ' + (page.getTitle() || 'Untitled page')"
                    [checked]="isPageChecked(page)"
                    (click)="$event.stopPropagation()"
                    (change)="onCheckboxChange(page)"
                  ></mat-checkbox>
                </span>
                <div class="page-title-area">
                  <span class="page-title">{{ page.getTitle() }}</span>
                  @if (page.getPageId()) {
                    <span class="page-subtitle">
                      <mat-chip class="sheet-id-chip" color="primary" selected disableRipple>
                        {{ page.getPageId() }}
                      </mat-chip>
                      <span class="sheet-discipline">{{ getSheetDiscipline(page.getPageId()) }}</span>
                    </span>
                  }
                </div>
              </div>
            </mat-list-item>
          }
        </mat-nav-list>
      </div>
    }

    @if (!loading && !loadingFileMetadata && pages.length === 0 && fileGroups.length === 0) {
      <div class="no-pages-message">
        <p>No pages ingested</p>
      </div>
    }

    <!-- Recursive template for region hierarchy nodes (Issue #1228) -->
    <ng-template #regionTree let-nodes let-page="page" let-fileId="fileId" let-depth="depth">
      <div class="region-sibling-group" role="presentation">
        @for (region of nodes; track region.path; let regionIndex = $index; let isLastRegion = $last) {
          <div
            class="region-list-item tree-depth-node tree-branch"
            role="treeitem"
            [attr.aria-level]="4 + depth"
            [attr.aria-selected]="isActiveRegion(region, page, fileId)"
            [attr.aria-expanded]="region.childNodesList?.length ? isRegionNodeExpanded(region, page, fileId) : null"
            [attr.aria-label]="regionAccessibleName(region)"
            [attr.data-tree-key]="regionTreeKey(region.path)"
            [attr.data-region-path]="region.path"
            [attr.data-region-depth]="depth"
            [attr.tabindex]="isTreeItemFocused(regionTreeKey(region.path)) ? 0 : -1"
            [class.region-active]="isActiveRegion(region, page, fileId)"
            [class.region-active-ancestor]="isRegionActiveAncestor(region, page, fileId)"
            [class.region-active-path-before]="isRegionBeforeActivePath(nodes, regionIndex, page, fileId)"
            [class.active-ancestor]="isRegionActiveAncestor(region, page, fileId)"
            [class.last-region-treeitem]="isLastRegion"
            [class.last-tree-sibling]="isLastRegion"
            [style.--tree-node-depth]="3 + depth"
            (focus)="onTreeItemFocus(regionTreeKey(region.path))"
            (keydown)="onTreeKeydown($event, 'region', undefined, page, region, fileId)"
            (click)="$event.stopPropagation(); onRegionClick(page, fileId, region)"
            (mouseenter)="onRegionHover(region)"
            (mouseleave)="onRegionHover(null)"
          >
            <div
              class="region-list-row tree-node-row"
              [class.tree-node-selected]="isActiveRegion(region, page, fileId)"
            >
              @if (region.childNodesList?.length > 0) {
                <button
                  type="button"
                  tabindex="-1"
                  class="tree-disclosure region-child-expand-btn"
                  [attr.aria-label]="
                    (isRegionNodeExpanded(region, page, fileId) ? 'Collapse ' : 'Expand ') +
                    regionAccessibleName(region)
                  "
                  (click)="toggleRegionExpand(page, fileId, region, $event)"
                  [attr.aria-expanded]="isRegionNodeExpanded(region, page, fileId)"
                >
                  <mat-icon aria-hidden="true" [class.expanded]="isRegionNodeExpanded(region, page, fileId)"
                    >chevron_right</mat-icon
                  >
                </button>
                <span class="tree-parent-connector" aria-hidden="true"></span>
                <span class="tree-child-tail" aria-hidden="true"></span>
              } @else {
                <span class="tree-parent-connector" aria-hidden="true"></span>
                <span class="region-leaf-spacer region-leaf-icon-frame" aria-hidden="true">
                  <mat-icon class="region-leaf-icon" fontSet="material-symbols-outlined">article</mat-icon>
                </span>
              }
              <div class="region-title-area">
                <span class="region-title">{{ region.title || region.slug }}</span>
                @if (region.ncsClassification) {
                  <span class="region-subtitle">{{ region.ncsClassification }}</span>
                }
              </div>
            </div>
            @if (!isLastRegion) {
              <span class="tree-sibling-continuation" aria-hidden="true"></span>
            }
            @if (region.childNodesList?.length > 0 && isRegionNodeExpanded(region, page, fileId)) {
              <div class="region-children-container" role="group" [@regionExpand]>
                <ng-container
                  *ngTemplateOutlet="
                    regionTree;
                    context: {
                      $implicit: region.childNodesList,
                      page: page,
                      fileId: fileId,
                      depth: depth + 1,
                    }
                  "
                ></ng-container>
              </div>
            }
          </div>
        }
      </div>
    </ng-template>

    <div class="resize-handle" (mousedown)="startResize($event)"></div>
  </mat-sidenav>
  <mat-sidenav-content>
    <ng-content></ng-content>

    <!-- Selected Pages Floating Toolbar -->
    <app-selected-pages-toolbar
      [selectedPages]="selectedPages"
      [visible]="selectedPages.length > 0"
      [drawerOpened]="opened"
      (action)="onSelectedPagesAction($event)"
    ></app-selected-pages-toolbar>
  </mat-sidenav-content>
</mat-sidenav-container>
